import { useEffect, useMemo, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import useAdminAction from '../hooks/useAdminAction';
import { ADMIN_ACTIONS } from '../constants/adminActions';
import { enrichGstKnowledgeBase, fetchGstMasterVersion, gstMasterVersionRef } from '../services/firestore';

function formatTimestamp(value) {
  if (!value) return 'Not set';
  if (typeof value.toDate === 'function') return value.toDate().toLocaleString('en-IN');
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not set' : date.toLocaleString('en-IN');
}

export default function GSTMaster() {
  const { performAction, loading: actionLoading, error: actionError } = useAdminAction();
  const [versionDoc, setVersionDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [newVersion, setNewVersion] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichmentResult, setEnrichmentResult] = useState(null);

  const currentVersion = versionDoc?.version || '';
  const trimmedVersion = newVersion.trim();
  const canSubmit = trimmedVersion && trimmedVersion !== currentVersion && confirmed && !actionLoading;

  const itemCountText = useMemo(() => {
    const count = Number(versionDoc?.itemCount || 0);
    return Number.isFinite(count) ? count.toLocaleString('en-IN') : '0';
  }, [versionDoc?.itemCount]);

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      setVersionDoc(await fetchGstMasterVersion());
    } catch (error) {
      setLoadError(error?.message || 'Could not load GST master version.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submitVersionUpdate = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    await performAction({
      action: ADMIN_ACTIONS.GST_MASTER_VERSION_UPDATED,
      targetBusinessId: null,
      before: {
        version: currentVersion || null,
        itemCount: Number(versionDoc?.itemCount || 0),
      },
      after: {
        version: trimmedVersion,
        itemCount: Number(versionDoc?.itemCount || 0),
      },
      writeFn: (batch, batchDb) => {
        batch.set(gstMasterVersionRef(batchDb), {
          version: trimmedVersion,
          updatedAt: Timestamp.now(),
          itemCount: Number(versionDoc?.itemCount || 0),
        }, { merge: true });
      },
    });

    setEnriching(true);
    setEnrichmentResult(null);
    try {
      setEnrichmentResult(await enrichGstKnowledgeBase(trimmedVersion));
    } catch (error) {
      setLoadError(error?.message || 'Version updated, but GST knowledge enrichment failed.');
    } finally {
      setEnriching(false);
    }

    setShowForm(false);
    setNewVersion('');
    setConfirmed(false);
    await load();
  };

  if (loading) return <div className="wb-card">Loading GST master...</div>;

  return (
    <>
      <section className="wb-card">
        <h3 className="wb-section-title">GST Master</h3>
        {loadError ? <p className="wb-error-text">{loadError}</p> : null}
        <div className="wb-kpi-grid">
          <div className="wb-kpi">
            <span>Current</span>
            <strong>{currentVersion || 'Not configured'}</strong>
          </div>
          <div className="wb-kpi">
            <span>Items</span>
            <strong>{itemCountText}</strong>
          </div>
          <div className="wb-kpi">
            <span>Last updated</span>
            <strong>{formatTimestamp(versionDoc?.updatedAt)}</strong>
          </div>
        </div>
        <button type="button" onClick={() => setShowForm((value) => !value)}>
          {showForm ? 'Cancel' : 'Update Version'}
        </button>
        {enriching ? <p className="wb-muted-text">Enriching GST knowledge base with Groq synonyms...</p> : null}
        {enrichmentResult ? (
          <p className="wb-muted-text">
            Knowledge base enriched: {Number(enrichmentResult.itemCount || 0).toLocaleString('en-IN')} records,
            {' '}{Number(enrichmentResult.categoriesEnriched || 0).toLocaleString('en-IN')} categories.
          </p>
        ) : null}
      </section>

      {showForm ? (
        <section className="wb-card">
          <h3 className="wb-section-title">Update Version</h3>
          <form onSubmit={submitVersionUpdate}>
            <label htmlFor="gst-master-version">New version string</label>
            <input
              id="gst-master-version"
              value={newVersion}
              onChange={(event) => setNewVersion(event.target.value)}
              placeholder="57th-council-2026-03-15"
              maxLength={80}
            />
            <label className="wb-checkbox-row">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
              />
              <span>I confirm GST rates in /gstMasterDB have been updated</span>
            </label>
            <button type="submit" disabled={!canSubmit}>
              {actionLoading ? 'Updating...' : 'Submit'}
            </button>
          </form>
          {actionError ? <p className="wb-error-text">{actionError}</p> : null}
        </section>
      ) : null}
    </>
  );
}
